import React from 'react'
import { Uuid } from '@common'
import { GoogleDriveSyncConfiguration } from '../types'
import {
  SyncConfigSection,
  SyncConfigCard,
  SyncConfigHeader,
  FolderName,
  TargetPath,
  SyncStatus,
  ActionButton,
  ButtonGroup,
  EmptyState
} from '../style'

interface SyncConfigurationPanelProps {
  configurations: GoogleDriveSyncConfiguration[]
  isSyncing: boolean
  onAddNew: () => void
  onDelete: (configId: Uuid) => void
  onSync: (configId?: Uuid) => void
  onToggleEnabled: (configId: Uuid, enabled: boolean) => void
  onOpenTargetPath?: (targetPath: string) => void
}

export const SyncConfigurationPanel: React.FC<SyncConfigurationPanelProps> = ({
  configurations,
  isSyncing,
  onAddNew,
  onDelete,
  onSync,
  onToggleEnabled,
  onOpenTargetPath
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  const formatSyncState = (config: GoogleDriveSyncConfiguration) => {
    if (config.syncError) return 'Needs attention'
    if (!config.syncEnabled) return 'Paused'
    switch (config.syncState) {
      case 'pending':
        return 'Pending first sync'
      case 'synced':
        return 'Synced'
      case 'updated':
        return 'Updated'
      case 'stale':
        return 'Stale in Drive'
      case 'deleted':
        return 'Removed from Drive'
      case 'error':
        return 'Needs attention'
      default:
        return config.lastSyncAt ? 'Synced' : 'Pending first sync'
    }
  }

  if (configurations.length === 0) {
    return (
      <SyncConfigSection>
        <EmptyState>
          <p>No folders configured for sync yet.</p>
          <ActionButton onClick={onAddNew} style={{ marginTop: '16px' }}>
            Add Folder to Sync
          </ActionButton>
        </EmptyState>
      </SyncConfigSection>
    )
  }

  return (
    <SyncConfigSection>
      <ButtonGroup style={{ justifyContent: 'space-between' }}>
        <ActionButton onClick={onAddNew}>
          Add Folder
        </ActionButton>
        <ActionButton
          variant="secondary"
          onClick={() => onSync()}
          disabled={isSyncing}
        >
          {isSyncing ? 'Syncing...' : 'Sync All'}
        </ActionButton>
      </ButtonGroup>

      {configurations.map(config => (
        <SyncConfigCard key={config.id}>
          <SyncConfigHeader>
            <div>
              <FolderName>{config.googleFolderName}</FolderName>
              <br />
              <TargetPath>
                Data Room target: {config.targetDataRoomPath}
                {config.syncSubfolders && ' (including subfolders)'}
              </TargetPath>
              {onOpenTargetPath && (
                <>
                  <br />
                  <ActionButton
                    variant="secondary"
                    onClick={() => onOpenTargetPath(config.targetDataRoomPath)}
                    style={{ marginTop: '10px', padding: '6px 10px' }}
                  >
                    Open target path
                  </ActionButton>
                </>
              )}
            </div>
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                onClick={() => onSync(config.id)}
                disabled={isSyncing || !config.syncEnabled}
              >
                Sync
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => onToggleEnabled(config.id, !config.syncEnabled)}
              >
                {config.syncEnabled ? 'Disable' : 'Enable'}
              </ActionButton>
              <ActionButton
                variant="danger"
                onClick={() => onDelete(config.id)}
              >
                Remove
              </ActionButton>
            </ButtonGroup>
          </SyncConfigHeader>
          <SyncStatus hasError={!!config.syncError}>
            {formatSyncState(config)}
            {' · '}
            Last synced: {formatDate(config.lastSyncAt)}
            {typeof config.fileCount === 'number' && ` · ${config.fileCount} file${config.fileCount === 1 ? '' : 's'}`}
            {config.syncError && ` · ${config.syncError}`}
          </SyncStatus>
        </SyncConfigCard>
      ))}
    </SyncConfigSection>
  )
}
